import React, { useState, useCallback, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { Grid, Button, CircularProgress } from '@mui/material';
import { Autocomplete, TextField, Typography, Paper } from '@mui/material';
import { DeleteOutlineRounded, EditRounded, DoneRounded } from '@mui/icons-material';
import { debounce, isFunction, isFinite, pick } from 'lodash';
import DebouncedTextField from './DebouncedTextField';

const containerStyle = {
  width: '100%',
  height: '400px',
};

const defaultCenter = {
  lat: 51.5015144, // Buckingham Palace
  lng: -0.1341506,
};

export default function LocationSearch({ title, initial, onChange }) {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
    region: 'UK',
  });

  const [editingLocation, setEditingLocation] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  useEffect(() => {
    setSelectedPlace(initial);
    if (isFinite(initial?.lat) && isFinite(initial?.lng)) {
      setMapCenter(pick(initial, ['lat', 'lng']));
    }
  }, [initial]);

  function formatLocation(location) {
    const result = {
      name: location.name,
      formatted_address: location.formatted_address,
      lat: location.geometry.location.lat(),
      lng: location.geometry.location.lng(),
    };

    return result;
  }

  // Fetch autocomplete suggestions
  const fetchSuggestions = useCallback((input) => {
    if (!window.google || !input) {
      setSuggestions([]);
      return;
    }

    const autocompleteService = new window.google.maps.places.AutocompleteService();
    autocompleteService.getPlacePredictions({ input, region: 'UK' }, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        setSuggestions(predictions || []);
      } else {
        setSuggestions([]);
        console.error('Autocomplete failed:', status);
      }
    });
  }, []);

  // eslint-disable-next-line
  const debouncedFetchSuggestions = useCallback(
    debounce((input) => fetchSuggestions(input), 800),
    [fetchSuggestions],
  );

  // Handle input change and fetch suggestions
  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    if (newInputValue) {
      debouncedFetchSuggestions(newInputValue);
    } else {
      setSuggestions([]);
    }
  };

  // Handle place selection
  const handlePlaceSelect = (event, newValue) => {
    if (!newValue) {
      setMapCenter(defaultCenter);
      return;
    }

    const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
    placesService.getDetails(
      {
        placeId: newValue.place_id,
        fields: ['name', 'formatted_address', 'geometry'],
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          selectPlace(formatLocation(place));
          setMapCenter({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        } else {
          console.error('Place details failed:', status);
          selectPlace(null);
        }
      },
    );
  };

  function selectPlace(place) {
    setSelectedPlace(place);
    isFunction(onChange) && onChange(place);
  }

  const handleMarkerDragEnd = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    selectPlace({ lat, lng, name: 'Location', formatted_address: `Lat: ${lat}, Lng: ${lng}` });
  };

  if (!isLoaded) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: 400 }}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid container>
        <Typography variant="subtitle1" gutterBottom>
          {title ?? 'Search for a location'}
        </Typography>
      </Grid>

      <Grid container sx={{ mb: 3, mt: 1 }}>
        <Autocomplete
          options={suggestions}
          getOptionLabel={(option) => option.description || ''}
          onInputChange={handleInputChange}
          inputValue={inputValue}
          onChange={handlePlaceSelect}
          renderInput={(params) => <TextField {...params} label="Enter location" variant="outlined" fullWidth />}
          fullWidth
        />
      </Grid>

      {selectedPlace && (
        <Grid container component={Paper} elevation={3} sx={{ p: 2, mb: 3 }} direction={'column'}>
          <Typography variant="h6" gutterBottom>
            Selected Location
          </Typography>

          {editingLocation ? (
            <DebouncedTextField
              value={selectedPlace.name}
              fullWidth
              variant="standard"
              onValueChange={(name) => selectPlace({ ...selectedPlace, name })}
              sx={{ mb: 1 }}
            />
          ) : (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {selectedPlace.name}
              </Typography>
            </>
          )}

          <Typography variant="body2" gutterBottom>
            {selectedPlace.formatted_address}
          </Typography>

          <Grid container>
            <Button
              onClick={() => {
                selectPlace(null);
                setMapCenter(defaultCenter);
                setInputValue('');
              }}
              startIcon={<DeleteOutlineRounded />}
              color="error"
              sx={{ mr: 1 }}
            >
              clear
            </Button>

            {editingLocation ? (
              <Button onClick={() => setEditingLocation(false)} startIcon={<DoneRounded />}>
                done
              </Button>
            ) : (
              <Button onClick={() => setEditingLocation(true)} startIcon={<EditRounded />}>
                edit
              </Button>
            )}
          </Grid>
        </Grid>
      )}

      <GoogleMap mapContainerStyle={containerStyle} center={mapCenter} zoom={14}>
        <Marker
          position={
            selectedPlace
              ? {
                  lat: selectedPlace.lat,
                  lng: selectedPlace.lng,
                }
              : defaultCenter
          }
          title={selectedPlace ? selectedPlace.name : ''}
          draggable
          onDragEnd={handleMarkerDragEnd}
          onDblClick={handleMarkerDragEnd}
          label="Drag to pick location"
        />
      </GoogleMap>
    </Grid>
  );
}
